/**
 * PUERTA DE SEGURIDAD OFFLINE (FASE 5.6 §9-§13, §26-§29, §43)
 *
 * Envuelve las tres rutas `/offline*` (ver `App.tsx`). Antes de montar
 * `children`, decide entre CUATRO pantallas — nunca deja pasar contenido
 * protegido mientras la decisión está pendiente (§42: desmontar, no tapar
 * con un modal):
 *
 *   1. perfil ya desbloqueado y coincide con la huella del usuario actual
 *      (o no hay forma de comprobar la huella porque no hay sesión online,
 *      ver más abajo) → renderiza `children` con la sesión offline activa;
 *   2. existe un perfil local (de la huella, o el `ultimoPerfilId` si no
 *      hay sesión online que validar) pero está BLOQUEADO → pantalla de
 *      PIN;
 *   3. no existe ningún perfil local para este usuario, y SÍ hay sesión
 *      online → "Protege los datos de este dispositivo" (alta);
 *   4. no existe ningún perfil local utilizable y NO hay sesión online →
 *      explica que hace falta conexión (§29).
 *
 * SESIÓN ONLINE AUSENTE, PERO PERFIL DESBLOQUEABLE (§13, E2E escenario C)
 *
 * Una recarga totalmente offline nunca completa `GET /api/auth/me`
 * (`contexto.tsx`), así que `usuario` es `null` aunque el dispositivo
 * tenga un perfil protegido y válido. Esto es CORRECTO — `usuario` refleja
 * fielmente si la sesión online se validó, nunca se finge uno para que
 * encaje (§13: "no confundir dimensiones"). En ese caso, esta puerta
 * ofrece desbloquear el ÚLTIMO perfil recordado (`perfil.ts::
 * ultimoPerfilId`, un UUID local opaco — nunca información de negocio) sin
 * exigir la huella: el PIN es la única prueba que hace falta para leer
 * datos ya autorizados en una sincronización anterior (§C/§10). El rol
 * efectivo, en ese caso, sale de `MetaOffline.rol` (cacheado), nunca de
 * `usuario.roles` — ver `offline/contextoPerfil.tsx`.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import type { Usuario } from '../api.ts';
import { Cargando } from '../componentes/base.tsx';
import { Marca } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { ProveedorPerfilOffline, type RolOffline } from '../offline/contextoPerfil.tsx';
import { leerMeta } from '../offline/db.ts';
import { comprobarLegado, descartarLegado, type EstadoLegado } from '../offline/legado.ts';
import {
  bloquearPerfilActivo,
  buscarPerfilPorFingerprint,
  crearPerfilLocal,
  desbloquearPerfil,
  eliminarPerfilLocal,
  ErrorPerfilBloqueado,
  fingerprintDeUsuario,
  limpiarPerfilBloqueadoSiNoHayActivo,
  listarPerfilesLocales,
  perfilActivo,
  prepararDesbloqueo,
  ultimoPerfilId,
  useEstadoBloqueoOffline,
  type EstadoBloqueoOffline,
  type PerfilLocal,
} from '../offline/perfil.ts';
import { revocarTodasLasUrlsTemporales } from '../offline/urlsTemporales.ts';

const LONGITUD_MINIMA_PIN = 6;
const PINES_TRIVIALES = new Set([
  '000000', '111111', '222222', '333333', '444444', '555555', '666666', '777777', '888888', '999999',
  '123456', '654321', '012345', '123123', '121212',
]);

function pinDemasiadoDebil(pin: string): boolean {
  if (pin.length < LONGITUD_MINIMA_PIN) return true;
  if (PINES_TRIVIALES.has(pin)) return true;
  return new Set(pin).size === 1; // "aaaaaa", "444444" con otra longitud, etc.
}

/**
 * Campo de PIN con botón mostrar/ocultar (§45 — obligatorio porque el PIN
 * no es puramente numérico: `pinDemasiadoDebil` acepta cualquier
 * carácter, así que un teclado numérico/`inputMode="numeric"` es sólo una
 * sugerencia, nunca una restricción real).
 */
function CampoPin({
  id,
  etiqueta,
  valor,
  onCambio,
  autoFocus,
  invalido,
}: {
  id: string;
  etiqueta: string;
  valor: string;
  onCambio: (v: string) => void;
  autoFocus?: boolean;
  invalido?: boolean;
}): ReactNode {
  const [visible, setVisible] = useState(false);
  return (
    <div className="campo">
      <label htmlFor={id}>{etiqueta}</label>
      <div className="fila" style={{ gap: 6, alignItems: 'center' }}>
        <input
          id={id}
          name={id}
          type={visible ? 'text' : 'password'}
          inputMode="numeric"
          autoComplete="off"
          autoFocus={autoFocus}
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
          aria-invalid={invalido}
        />
        <button
          type="button"
          className="pequeno"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? `Ocultar ${etiqueta.toLowerCase()}` : `Mostrar ${etiqueta.toLowerCase()}`}
        >
          {visible ? 'Ocultar' : 'Mostrar'}
        </button>
      </div>
    </div>
  );
}

let contadorAliasSugerido = 0;
function aliasSugerido(): string {
  contadorAliasSugerido += 1;
  return `Perfil local ${String(contadorAliasSugerido)}`;
}

// ==================================================================== puerta

export function PuertaOfflineSegura({ children }: { children: ReactNode }): ReactNode {
  const { usuario } = useSesion();
  const estadoBloqueo = useEstadoBloqueoOffline();
  const [resolviendo, setResolviendo] = useState(true);
  const [sinPerfilUtilizable, setSinPerfilUtilizable] = useState(false);
  const [mostrarSelector, setMostrarSelector] = useState(false);
  const [intento, setIntento] = useState(0);

  //  §28 — eliminar el perfil ACTIVO (`PanelGestionPerfilOffline`) deja
  //  `estadoBloqueo` en `null` (ni LOCKED ni UNLOCKED: no hay ningún
  //  perfil concreto del que hablar). Sin este efecto, la puerta se
  //  quedaría sin ninguna rama que renderizar — nunca una pantalla en
  //  blanco: se fuerza a repetir la resolución completa (que, sin perfil
  //  que emparejar, acabará ofreciendo login/alta de nuevo).
  //
  //  SÓLO en la transición UNLOCKED -> null, nunca en un simple
  //  "estadoBloqueo es null ahora" — `estadoBloqueo` EMPIEZA en `null`
  //  para cualquier usuario de primera vez (nunca se preparó ningún
  //  perfil para desbloquear) y sigue siéndolo mientras la resolución
  //  normal desemboca en "sin perfil, ofrecer alta" (`sinPerfilUtilizable`).
  //  Disparar aquí sin distinguir ese caso reiniciaba la resolución async
  //  (huella + `indexedDB.open`) sin parar — bucle infinito real, detectado
  //  contra un navegador de verdad. El `ref` guarda el valor ANTERIOR
  //  precisamente para poder exigir que antes SÍ hubiera un perfil
  //  desbloqueado.
  //
  //  FASE 5.7-C H15-F — se INTENTÓ ampliar esta condición a "LOCKED O
  //  UNLOCKED -> null" para cubrir también "borrar un perfil LOCKED que
  //  nunca se desbloqueó esta sesión" (`PantallaPinOlvidado` ->
  //  `eliminarPerfilLocal`, ver su cabecera). Revertido: ese caso general
  //  choca con `limpiarPerfilBloqueadoSiNoHayActivo` (C11), que TAMBIÉN
  //  deja una transición LOCKED -> null cuando limpia el LOCKED huérfano
  //  de un usuario ANTERIOR mientras resuelve a uno nuevo sin perfil
  //  propio — un evento sin ninguna relación con "el perfil que se estaba
  //  mostrando desapareció", pero indistinguible de él sólo mirando la
  //  forma del estado. Ampliar el `ref` así causaba un `setIntento`
  //  espurio justo cuando Eva resolvía "sin perfil propio" (con el LOCKED
  //  de Pepe todavía sin limpiar de una sesión anterior), reabriendo la
  //  resolución a mitad y devolviendo un parpadeo real a
  //  "Comprobando…" — detectado por
  //  `offline-perfil-multiusuario.test.tsx` (CASO E). El caso de borrar un
  //  perfil LOCKED se resuelve en su lugar de forma EXPLÍCITA, más abajo
  //  (`onDesbloqueado`), en vez de inferirlo de la forma del estado.
  const estadoBloqueoAnterior = useRef<EstadoBloqueoOffline | null>(null);
  useEffect(() => {
    const anterior = estadoBloqueoAnterior.current;
    estadoBloqueoAnterior.current = estadoBloqueo;
    if (anterior?.estado === 'UNLOCKED' && estadoBloqueo === null) setIntento((n) => n + 1);
  }, [estadoBloqueo]);

  //  §41 — cualquier `blob:` creado a partir de un documento/evidencia
  //  DESCIFRADO deja de poder resolverse en cuanto el perfil deja de
  //  estar UNLOCKED (bloqueo manual, logout, cambio de perfil, TTL no
  //  cuenta — eso no bloquea). Un solo punto para las tres rutas
  //  `/offline*`, en vez de que cada pantalla lleve su propia cuenta.
  useEffect(() => {
    if (estadoBloqueo?.estado !== 'UNLOCKED') revocarTodasLasUrlsTemporales();
  }, [estadoBloqueo]);

  useEffect(() => {
    let vivo = true;
    setResolviendo(true);
    setSinPerfilUtilizable(false);

    void (async () => {
      const activo = perfilActivo();
      const huella = usuario === null ? null : await fingerprintDeUsuario(usuario.id);
      if (!vivo) return;

      //  Ya desbloqueado (navegación interna entre pantallas /offline) y,
      //  si hay huella que comprobar, coincide.
      if (activo !== null && (huella === null || activo.fingerprintUsuario === huella)) {
        setResolviendo(false);
        return;
      }
      //  Desbloqueado pero de OTRO usuario (cambio de sesión online sin
      //  pasar por logout explícito, p.ej. dos pestañas) — nunca se sigue
      //  usando ese perfil por error.
      if (activo !== null) bloquearPerfilActivo();

      const objetivo =
        huella !== null
          ? await buscarPerfilPorFingerprint(huella)
          : await (async () => {
              const id = ultimoPerfilId();
              const porUltimoId = id === null ? null : await listarPerfilesLocales().then((todos) => todos.find((p) => p.perfilId === id) ?? null);
              if (porUltimoId !== null) return porUltimoId;
              //  FASE 5.7-C C09 — `ultimoPerfilId()` es sólo un ATAJO (una
              //  clave suelta de `localStorage`, mucho más frágil ante una
              //  limpieza de almacenamiento del navegador que la propia
              //  base `neatos-perfiles`, IndexedDB): si falta o ya no
              //  coincide con ningún perfil real, pero el directorio SÍ
              //  tiene EXACTAMENTE un perfil, es casi con toda seguridad
              //  el de este dispositivo — se ofrece directamente en vez
              //  de decir "no hay ningún perfil" cuando sí lo hay (repro
              //  real: cerrar el navegador del todo en iOS Safari puede
              //  perder esa clave suelta sin perder la base). Con DOS o
              //  más perfiles nunca se adivina cuál es "el mío" —sigue
              //  disponible el enlace "Ver otros perfiles"/"Ya tengo un
              //  perfil" de las pantallas de abajo, que sí consulta el
              //  directorio completo.
              const todos = await listarPerfilesLocales();
              return todos.length === 1 ? todos[0]! : null;
            })();
      if (!vivo) return;
      if (objetivo === null) {
        //  FASE 5.7-C C11 — sin esto, un LOCKED de una identidad ANTERIOR
        //  (p.ej. el logout de Pepe, que deja su perfil LOCKED en memoria)
        //  sobrevivía sin cambios aunque la identidad actual (Eva) no
        //  tuviera perfil propio: la rama de abajo (`estadoBloqueo?.estado
        //  === 'LOCKED'`) seguía viendo el de Pepe y nunca se llegaba a
        //  ofrecer "crear perfil de Eva". Ver `limpiarPerfilBloqueadoSiNoHayActivo`.
        limpiarPerfilBloqueadoSiNoHayActivo();
        setSinPerfilUtilizable(true);
        setResolviendo(false);
        return;
      }
      await prepararDesbloqueo(objetivo.perfilId);
      if (vivo) setResolviendo(false);
    })();

    return () => {
      vivo = false;
    };
  }, [usuario, intento]);

  if (resolviendo) return <Cargando texto="Comprobando datos offline de este dispositivo…" />;

  if (mostrarSelector) {
    return (
      <SelectorPerfiles
        onElegido={() => setMostrarSelector(false)}
        onCancelar={estadoBloqueo !== null ? () => setMostrarSelector(false) : undefined}
      />
    );
  }

  if (estadoBloqueo?.estado === 'UNLOCKED') {
    return (
      <ConSesionOfflineResuelta perfilId={estadoBloqueo.perfilId} usuario={usuario}>
        {children}
      </ConSesionOfflineResuelta>
    );
  }

  if (estadoBloqueo?.estado === 'LOCKED') {
    return (
      <PantallaDesbloquear
        perfilId={estadoBloqueo.perfilId}
        aliasLocal={estadoBloqueo.aliasLocal}
        onDesbloqueado={() => undefined /* el propio estadoBloqueo ya dispara el re-render */}
        onPerfilEliminado={() => setIntento((n) => n + 1)}
        onCambiarPerfil={() => setMostrarSelector(true)}
        idUsuarioOnline={usuario?.id ?? null}
      />
    );
  }

  if (sinPerfilUtilizable) {
    if (usuario !== null) {
      return (
        <PantallaConfigurarPin
          idUsuario={usuario.id}
          onListo={() => undefined}
          onCambiarPerfil={() => setMostrarSelector(true)}
        />
      );
    }
    return <PantallaSinConexionSinPerfil onCambiarPerfil={() => setMostrarSelector(true)} />;
  }

  return <Cargando texto="Comprobando datos offline de este dispositivo…" />;
}

/**
 * Resuelve el ROL efectivo (§43 de `contextoPerfil.tsx`) antes de montar
 * `children` — nunca los monta con un rol a medio calcular.
 *
 * `usuario.roles` SÓLO es correcto cuando el perfil desbloqueado es el
 * SUYO (huella coincide) — nunca cuando hay sesión online de un usuario
 * pero, vía "Cambiar perfil" (§27, dispositivo compartido), lo que está
 * desbloqueado es el perfil de OTRO usuario. En ese caso el único rol
 * correcto es el cacheado en el propio perfil (`MetaOffline.rol`), igual
 * que en la recarga totalmente offline (`usuario === null`).
 *
 * F14.2 (OFF-F142-ROLES-AL-CAMBIAR-PERFIL) — los roles resueltos van atados al `perfilId` para el que se
 * resolvieron: si el perfil desbloqueado cambia sin pasar por un render bloqueado (A desbloqueado → B desbloqueado),
 * nunca se pinta B ni un instante con los roles de A. Antes, el estado anterior sobrevivía un render.
 */
function ConSesionOfflineResuelta({
  perfilId,
  usuario,
  children,
}: {
  perfilId: string;
  usuario: Usuario | null;
  children: ReactNode;
}): ReactNode {
  const [resuelto, setResuelto] = useState<{ perfilId: string; roles: readonly RolOffline[] } | null>(null);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      const activo = perfilActivo();
      const coincideConLaSesionOnline =
        usuario !== null && activo !== null && activo.perfilId === perfilId && activo.fingerprintUsuario === (await fingerprintDeUsuario(usuario.id));
      if (!vivo) return;
      if (coincideConLaSesionOnline) {
        setResuelto({ perfilId, roles: usuario!.roles });
        return;
      }
      //  F14.2 — la sesión puede cambiar de usuario con este perfil desbloqueado y la puerta bloquearlo mientras
      //  tanto: entonces no hay nada que resolver (se pinta el desbloqueo), nunca un rechazo sin capturar.
      let meta: Awaited<ReturnType<typeof leerMeta>>;
      try {
        meta = await leerMeta(perfilId);
      } catch (e) {
        if (e instanceof ErrorPerfilBloqueado) return;
        throw e;
      }
      if (vivo) setResuelto({ perfilId, roles: meta === null ? [] : [{ rol: meta.rol === 'TECNICO' ? 'TECNICO_MANTENIMIENTO' : meta.rol, idCentro: null }] });
    })();
    return () => {
      vivo = false;
    };
  }, [perfilId, usuario]);

  if (resuelto === null || resuelto.perfilId !== perfilId) return <Cargando texto="Cargando datos offline…" />;
  return <ProveedorPerfilOffline valor={{ perfilId, roles: resuelto.roles }}>{children}</ProveedorPerfilOffline>;
}

// ================================================================ selector

export function SelectorPerfiles({
  onElegido,
  onCancelar,
}: {
  onElegido: () => void;
  onCancelar?: () => void;
}): ReactNode {
  const [perfiles, setPerfiles] = useState<PerfilLocal[] | null>(null);
  const [elegido, setElegido] = useState<PerfilLocal | null>(null);

  useEffect(() => {
    let vivo = true;
    void listarPerfilesLocales().then((p) => {
      if (vivo) setPerfiles([...p].sort((a, b) => b.ultimoUsoEn.localeCompare(a.ultimoUsoEn)));
    });
    return () => {
      vivo = false;
    };
  }, []);

  if (elegido !== null) {
    return (
      <PantallaDesbloquear
        perfilId={elegido.perfilId}
        aliasLocal={elegido.aliasLocal}
        onDesbloqueado={onElegido}
        //  Nunca alcanzable de verdad hoy: "He olvidado mi PIN" exige
        //  `idUsuarioOnline !== null` para ofrecer algo más que "necesitas
        //  conexión" (ver `PantallaPinOlvidado`), y aquí SIEMPRE se pasa
        //  `null` (elegir perfil por selector es, a propósito, un camino
        //  sin sesión online que lo respalde). Volver al listado es la
        //  respuesta razonable si eso cambiara alguna vez.
        onPerfilEliminado={() => setElegido(null)}
        onCambiarPerfil={() => setElegido(null)}
        idUsuarioOnline={null}
      />
    );
  }

  return (
    <div className="login">
      <div className="login-caja">
        <div className="login-marca">
          <Marca />
        </div>
        <h1>Perfiles locales de este dispositivo</h1>
        {perfiles === null && <Cargando texto="Cargando…" />}
        {perfiles !== null && perfiles.length === 0 && (
          <p className="suave">Este dispositivo todavía no tiene ningún perfil offline protegido.</p>
        )}
        {perfiles !== null && perfiles.length > 0 && (
          <ul className="lista-perfiles" style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {perfiles.map((p) => (
              <li key={p.perfilId}>
                <button
                  type="button"
                  className="ancho"
                  onClick={() => {
                    void prepararDesbloqueo(p.perfilId).then(() => setElegido(p));
                  }}
                >
                  {p.aliasLocal}
                </button>
              </li>
            ))}
          </ul>
        )}
        {onCancelar !== undefined && (
          <button type="button" className="enlace" style={{ marginTop: 14 }} onClick={onCancelar}>
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================== desbloquear

function PantallaDesbloquear({
  perfilId,
  aliasLocal,
  onDesbloqueado,
  onPerfilEliminado,
  onCambiarPerfil,
  idUsuarioOnline,
}: {
  perfilId: string;
  aliasLocal: string;
  onDesbloqueado: () => void;
  /**
   * FASE 5.7-C H15-F — DELIBERADAMENTE distinto de `onDesbloqueado`: un
   * desbloqueo real dispara desde `estadoBloqueo` (LOCKED -> UNLOCKED) su
   * propio re-render correcto sin ayuda ("Datos sin conexión" es una rama
   * de render directa), así que forzar aquí una repetición de la
   * resolución sería, en el mejor caso, un trabajo redundante y, en el
   * peor (comprobado con un test real), una carrera visible con
   * `limpiarPerfilBloqueadoSiNoHayActivo` (C11) que provoca un parpadeo a
   * "Comprobando…" — ver el comentario largo de `estadoBloqueoAnterior`
   * más arriba. "Eliminar perfil local y empezar de nuevo"
   * (`PantallaPinOlvidado`) es un caso distinto de verdad: dispara una
   * transición LOCKED -> `null` para la que NINGUNA rama de render
   * directa existe (ni LOCKED ni UNLOCKED), así que aquí SÍ hace falta
   * pedir explícitamente que se repita la resolución completa.
   */
  onPerfilEliminado: () => void;
  onCambiarPerfil: () => void;
  idUsuarioOnline: string | null;
}): ReactNode {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [intentos, setIntentos] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [pantallaOlvido, setPantallaOlvido] = useState(false);

  //  Bloqueo PROGRESIVO en UI (§10) — defensa OPERATIVA, nunca
  //  criptográfica: nunca impide reintentar para siempre, sólo introduce
  //  fricción y ofrece con más insistencia la recuperación por PIN
  //  olvidado tras varios fallos.
  const LIMITE_AVISO = 5;

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await desbloquearPerfil(perfilId, pin);
      onDesbloqueado();
    } catch {
      //  §11 — error genérico siempre, nunca distinto de cualquier otro
      //  fallo de descifrado; nunca se borra ni se altera el perfil.
      setIntentos((n) => n + 1);
      setError('PIN incorrecto.');
      setPin('');
    } finally {
      setEnviando(false);
    }
  }

  if (pantallaOlvido) {
    return (
      <PantallaPinOlvidado
        perfilId={perfilId}
        idUsuarioOnline={idUsuarioOnline}
        onCancelar={() => setPantallaOlvido(false)}
        onPerfilRecreado={onPerfilEliminado}
      />
    );
  }

  return (
    <div className="login">
      <div className="login-caja">
        <div className="login-marca">
          <Marca />
        </div>
        <h1>Desbloquear datos offline</h1>
        <p className="suave">{aliasLocal}</p>
        <form onSubmit={(e) => void enviar(e)} noValidate>
          {error !== null && (
            <div className="nota error" role="alert" aria-live="assertive">
              {error}
            </div>
          )}
          <CampoPin id="pin-desbloqueo" etiqueta="PIN" valor={pin} onCambio={setPin} autoFocus invalido={error !== null} />
          <button type="submit" className="principal ancho" disabled={enviando || pin === ''}>
            {enviando ? 'Comprobando…' : 'Desbloquear'}
          </button>
        </form>
        {intentos >= LIMITE_AVISO && (
          <p className="nota" style={{ marginTop: 10 }}>
            Varios intentos sin éxito. Si no recuerdas tu PIN, puedes recuperarlo.
          </p>
        )}
        <div className="fila" style={{ marginTop: 14, gap: 16, flexWrap: 'wrap' }}>
          <button type="button" className="enlace" onClick={onCambiarPerfil}>
            Cambiar perfil
          </button>
          <button type="button" className="enlace" onClick={() => setPantallaOlvido(true)}>
            He olvidado mi PIN
          </button>
        </div>
      </div>
    </div>
  );
}

// =========================================================== pin olvidado

/**
 * §29. Nunca recupera la DEK anterior — la única salida es eliminar este
 * perfil local y volver a protegerlo desde cero. Exige sesión online real
 * (la propia identidad del usuario es la única "prueba" que sustituye al
 * PIN perdido — nunca una pregunta secreta ni una recovery key, §E).
 */
function PantallaPinOlvidado({
  perfilId,
  idUsuarioOnline,
  onCancelar,
  onPerfilRecreado,
}: {
  perfilId: string;
  idUsuarioOnline: string | null;
  onCancelar: () => void;
  onPerfilRecreado: () => void;
}): ReactNode {
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (idUsuarioOnline === null) {
    return (
      <div className="login">
        <div className="login-caja">
          <div className="login-marca">
            <Marca />
          </div>
          <h1>He olvidado mi PIN</h1>
          <p>Necesitas conexión para volver a configurar el acceso offline de este dispositivo.</p>
          <button type="button" className="enlace" onClick={onCancelar}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  async function eliminarYEmpezarDeNuevo(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await eliminarPerfilLocal(perfilId);
      onPerfilRecreado();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se ha podido eliminar el perfil local.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="login">
      <div className="login-caja">
        <div className="login-marca">
          <Marca />
        </div>
        <h1>He olvidado mi PIN</h1>
        <p>
          Sin el PIN no es posible recuperar los datos offline cifrados de este perfil — no existe una clave de recuperación
          guardada en ningún sitio (ni en este dispositivo ni en el servidor).
        </p>
        <p>
          Para continuar hay que eliminar el perfil local de este dispositivo y protegerlo de nuevo con un PIN nuevo. Los
          cambios sin sincronizar que tuviera este perfil se perderán de forma irreversible.
        </p>
        {error !== null && (
          <div className="nota error" role="alert">
            {error}
          </div>
        )}
        {!confirmando ? (
          <div className="fila" style={{ gap: 10, marginTop: 10 }}>
            <button type="button" className="peligro" onClick={() => setConfirmando(true)}>
              Eliminar perfil local y empezar de nuevo
            </button>
            <button type="button" onClick={onCancelar}>
              Cancelar
            </button>
          </div>
        ) : (
          <div className="fila" style={{ gap: 10, marginTop: 10 }}>
            <button type="button" className="peligro" disabled={enviando} onClick={() => void eliminarYEmpezarDeNuevo()}>
              {enviando ? 'Eliminando…' : 'Sí, eliminar definitivamente'}
            </button>
            <button type="button" onClick={() => setConfirmando(false)}>
              No, cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ========================================================= sin conexión

function PantallaSinConexionSinPerfil({ onCambiarPerfil }: { onCambiarPerfil: () => void }): ReactNode {
  return (
    <div className="login">
      <div className="login-caja">
        <div className="login-marca">
          <Marca />
        </div>
        <h1>Sin datos offline en este dispositivo</h1>
        <p>Necesitas conexión para proteger este dispositivo y descargar tus datos por primera vez.</p>
        <button type="button" className="enlace" onClick={onCambiarPerfil}>
          Ver otros perfiles de este dispositivo
        </button>
      </div>
    </div>
  );
}

// ======================================================= configurar (alta)

function PantallaConfigurarPin({
  idUsuario,
  onListo,
  onCambiarPerfil,
}: {
  idUsuario: string;
  onListo: () => void;
  onCambiarPerfil: () => void;
}): ReactNode {
  const [legado, setLegado] = useState<EstadoLegado | 'comprobando'>('comprobando');
  const [legadoResuelto, setLegadoResuelto] = useState(false);
  const [alias, setAlias] = useState(() => aliasSugerido());
  const [pin, setPin] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false);

  useEffect(() => {
    let vivo = true;
    void comprobarLegado(idUsuario).then((r) => {
      if (vivo) setLegado(r);
    });
    return () => {
      vivo = false;
    };
  }, [idUsuario]);

  const hayLegadoBloqueante = legado !== 'comprobando' && legado.existe && legado.mutacionesSinResolver > 0 && !legadoResuelto;

  async function descartarYContinuar(): Promise<void> {
    setEnviando(true);
    try {
      await descartarLegado(idUsuario);
      setLegadoResuelto(true);
    } finally {
      setEnviando(false);
    }
  }

  async function crear(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (pinDemasiadoDebil(pin)) {
      setError(`Elige un PIN de al menos ${String(LONGITUD_MINIMA_PIN)} caracteres, que no sea una secuencia obvia.`);
      return;
    }
    if (pin !== confirmacion) {
      setError('Los dos PIN no coinciden.');
      return;
    }
    setEnviando(true);
    try {
      //  Legado presente pero SIN mutaciones sin resolver — se limpia en
      //  silencio justo antes de crear el perfil nuevo (§23/§24): nunca
      //  queda como fallback plaintext.
      if (legado !== 'comprobando' && legado.existe && legado.mutacionesSinResolver === 0) {
        await descartarLegado(idUsuario);
      }
      await crearPerfilLocal(idUsuario, pin, alias.trim() === '' ? aliasSugerido() : alias.trim());
      onListo();
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : 'No se ha podido proteger este dispositivo.');
    } finally {
      setEnviando(false);
    }
  }

  if (legado === 'comprobando') return <Cargando texto="Comprobando este dispositivo…" />;

  if (hayLegadoBloqueante) {
    return (
      <div className="login">
        <div className="login-caja">
          <div className="login-marca">
            <Marca />
          </div>
          <h1>Antes de proteger este dispositivo</h1>
          <p>
            Este dispositivo tiene {legado.mutacionesSinResolver} cambio(s) sin sincronizar de una versión anterior de la
            aplicación, que ya no puede leerse aquí.
          </p>
          <p>Para continuar hay que descartarlos — no podrán recuperarse.</p>
          {!confirmandoDescarte ? (
            <div className="fila" style={{ gap: 10, marginTop: 10 }}>
              <button type="button" className="peligro" onClick={() => setConfirmandoDescarte(true)}>
                Descartar cambios pendientes
              </button>
            </div>
          ) : (
            <div className="fila" style={{ gap: 10, marginTop: 10 }}>
              <button type="button" className="peligro" disabled={enviando} onClick={() => void descartarYContinuar()}>
                {enviando ? 'Descartando…' : 'Sí, descartar definitivamente'}
              </button>
              <button type="button" onClick={() => setConfirmandoDescarte(false)}>
                Cancelar
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <div className="login-caja">
        <div className="login-marca">
          <Marca />
        </div>
        <h1>Protege los datos de este dispositivo</h1>
        <p className="suave">
          Los datos que descargues para trabajar sin conexión quedarán cifrados en este dispositivo. Un PIN local los
          protege — no sustituye tu contraseña, y sólo sirve en este dispositivo.
        </p>
        <form onSubmit={(e) => void crear(e)} noValidate>
          {error !== null && (
            <div className="nota error" role="alert" aria-live="assertive">
              {error}
            </div>
          )}
          <div className="campo">
            <label htmlFor="alias-perfil">Nombre de este perfil (visible antes de desbloquear)</label>
            <input id="alias-perfil" name="alias" type="text" value={alias} onChange={(e) => setAlias(e.target.value)} maxLength={40} />
          </div>
          <CampoPin id="pin-nuevo" etiqueta="Crear PIN" valor={pin} onCambio={setPin} invalido={error !== null} />
          <CampoPin
            id="pin-confirmar"
            etiqueta="Confirmar PIN"
            valor={confirmacion}
            onCambio={setConfirmacion}
            invalido={error !== null}
          />
          <button type="submit" className="principal ancho" disabled={enviando}>
            {enviando ? 'Protegiendo…' : 'Proteger este dispositivo'}
          </button>
        </form>
        <button type="button" className="enlace" style={{ marginTop: 14 }} onClick={onCambiarPerfil}>
          Ya tengo un perfil en este dispositivo
        </button>
      </div>
    </div>
  );
}

// ==================================================== gestión (§27/§28)

/**
 * Panel para usar DENTRO de una pantalla ya desbloqueada (`PaginaOffline`)
 * — "Cambiar perfil" (§27) y "Eliminar datos offline de este perfil" (§28).
 * No decide navegación por sí solo: `alCambiarPerfil` es responsabilidad
 * de quien lo monta (recargar la puerta, que volverá a mostrar el
 * selector en cuanto detecte que ya no hay perfil activo).
 */
export function PanelGestionPerfilOffline({
  perfilId,
  aliasLocal,
  pendientes,
  alCambiarPerfil,
}: {
  perfilId: string;
  aliasLocal: string;
  pendientes: number;
  alCambiarPerfil: () => void;
}): ReactNode {
  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);
  const [enviando, setEnviando] = useState(false);

  function cambiarPerfil(): void {
    bloquearPerfilActivo();
    alCambiarPerfil();
  }

  async function eliminar(): Promise<void> {
    setEnviando(true);
    try {
      await eliminarPerfilLocal(perfilId);
      alCambiarPerfil();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Este dispositivo</h2>
      </div>
      <div className="panel-cuerpo">
        <p className="suave">Perfil local: {aliasLocal}</p>
        <div className="fila" style={{ gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={cambiarPerfil}>
            Cambiar perfil
          </button>
          {!confirmandoEliminar ? (
            <button type="button" className="peligro" onClick={() => setConfirmandoEliminar(true)}>
              Eliminar datos offline de este perfil
            </button>
          ) : null}
        </div>
        {confirmandoEliminar && (
          <div className="nota error" style={{ marginTop: 10 }}>
            <p>
              Esto elimina, sólo en este dispositivo, los datos offline de este perfil
              {pendientes > 0 ? ` — incluidos ${String(pendientes)} cambio(s) todavía sin sincronizar, que se perderán de forma irreversible` : ''}.
              No afecta al servidor ni a otros perfiles.
            </p>
            <div className="fila" style={{ gap: 10 }}>
              <button type="button" className="peligro" disabled={enviando} onClick={() => void eliminar()}>
                {enviando ? 'Eliminando…' : 'Sí, eliminar'}
              </button>
              <button type="button" onClick={() => setConfirmandoEliminar(false)}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
